/**
 * index.jsx
 * desc: application entry point (built by Vite)
 * note: responsible for inserting application into DOM
 */
'use strict';

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import { version } from '../package.json';
import Keycloak from './components/Keycloak';
import App from './app';
import store from './store';
import * as Sentry from "@sentry/react";
import { beforeSend } from './utilities/sentry';
import './assets/styles/tokens.css';
import './assets/styles/base.css';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import './assets/styles/toastr-overrides.css';
import './assets/styles/global.css';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';


// set version on window object
// note: used for debugging and tracking purposes
window.APP_VERSION = `v${version}`;

// hand the CSP nonce to react-remove-scroll (Radix Dialog's scroll lock): it injects a
// <style> tag and only takes a nonce from this webpack-era global, which Vite never sets —
// without it style-src blocks the tag and the page behind a dialog keeps scrolling
window.__webpack_nonce__ = document.querySelector('[data-gtm-nonce]')?.dataset.gtmNonce;

// recover from a failed lazy-chunk load (transient network failure, or hashed assets
// replaced by a deploy while this tab was open) by reloading to pick up the current
// index.html. Time-guarded: if a reload already happened in the last minute the asset
// is genuinely broken, so let the error propagate to the route ErrorBoundary instead
// of reload-looping.
const PRELOAD_ERROR_RELOAD_KEY = 'ym.preloadErrorReloadedAt';
window.addEventListener('vite:preloadError', (event) => {
    const lastReloadAt = Number(sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY)) || 0;
    if (Date.now() - lastReloadAt < 60000) return;
    sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, String(Date.now()));
    event.preventDefault();
    window.location.reload();
});

// setup Sentry
if (window.Ym.SENTRY_DSN) {
    Sentry.init({
        dsn: window.Ym.SENTRY_DSN,
        // build-and-push.yaml uploads this build's sourcemaps under the same name
        // (package.json is synced to the release version before the image build)
        release: `web-app@${version}`,
        environment: window.location.hostname,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.consoleLoggingIntegration({ levels: [ 'warn', 'error' ] }),
        ],
        sendClientReports: false,
        enableLogs: true,
        // discard errors thrown entirely inside injected third-party scripts
        // (browser extensions monkey-patching builtins) — see utilities/sentry
        beforeSend,
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        tracesSampleRate: 1.0
    });
}

// initialize application
const container = document.getElementById('root');
// note: React 19 routes render errors through these root options instead of
// re-throwing to window; without them, errors caught by error boundaries
// (incl. the router errorElement) never reach Sentry
const root = createRoot(container, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
});
root.render((
    <Keycloak>
        <Provider store={ store }>
            <App />
            <ReduxToastr newestOnTop={ false } preventDuplicates={ true }/>
        </Provider>
    </Keycloak>
));
